"use client";

import { useState } from "react";
import Image from "next/image";
import NextLink from "next/link";
import { useFormContext, useWatch } from "react-hook-form";
import { Button, Spinner } from "@cappelendamm/design-system/react";
import { ArrowLeft } from "iconoir-react";
import { getImageBySize } from "@cappelendamm/catalogue/crystallize/utils/images.js";
import {
  getGiftSelectionStatus,
  type GiftSelectionStatus,
} from "./giftSelectionStatus";
import type { GiftSelectionFormValues } from "./GiftSelectorForm";

// The button looks disabled until everything is picked, but stays focusable:
// pressing it runs the validation, which shows what is missing and scrolls there.
const INACTIVE_BUTTON =
  "aria-disabled:cursor-not-allowed aria-disabled:[--cvr-button-background-hover:var(--cvr-button-background-disabled)] aria-disabled:[--cvr-button-background:var(--cvr-button-background-disabled)] aria-disabled:[--cvr-button-contrast:var(--cvr-button-contrast-disabled)]";

type GiftSelectionBarViewProps = {
  status: GiftSelectionStatus;
  backHref: string;
  /** The next page is loading after a valid submit. */
  isPending?: boolean;
};

export const GiftSelectionBarView = ({
  status: { slotGroups, isComplete, text },
  backHref,
  isPending = false,
}: GiftSelectionBarViewProps) => {
  const hasItems = slotGroups.length > 0;
  const isInactive = !isComplete;

  return (
    <div className="sticky bottom-0 z-20 -mx-4 border-t border-stroke-default bg-white px-4 pt-3 pb-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] sm:mx-0 sm:rounded-t-2xl sm:border-x sm:px-6 sm:pt-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        {hasItems ? (
          <div aria-hidden="true" className="flex items-center gap-3">
            <span className="shrink-0 text-sm font-semibold sm:text-base">
              Din pakke:
            </span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {slotGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="flex gap-1.5">
                  {group.map(({ option }, slotIndex) => {
                    const src = option
                      ? getImageBySize(option.images, 200).src
                      : undefined;

                    return option && src ? (
                      <Image
                        key={option.sku}
                        src={src}
                        alt=""
                        width={40}
                        height={40}
                        className="size-10 rounded-md border border-stroke-default bg-white object-contain p-0.5 transition-opacity duration-300 starting:opacity-0"
                      />
                    ) : (
                      <span
                        key={slotIndex}
                        className="size-10 rounded-md border-2 border-dashed border-gray-400"
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {hasItems ? (
          <p aria-live="polite" className="sr-only">
            {isPending ? "Laster" : text}
          </p>
        ) : null}

        <div className="flex items-center gap-3">
          <Button
            as={NextLink}
            href={backHref}
            variant="secondary"
            aria-label="Gå tilbake"
            className="size-12 min-w-0 shrink-0 p-0 sm:w-auto sm:px-6"
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            <span className="hidden sm:inline">Gå tilbake</span>
          </Button>
          <Button
            type="submit"
            variant="primary"
            aria-disabled={isInactive || undefined}
            aria-busy={isPending || undefined}
            className={`relative flex-1 sm:min-w-64 sm:flex-none ${INACTIVE_BUTTON}`}
          >
            <span className={isPending ? "opacity-0" : undefined}>
              {isInactive ? text : "Gå videre til bestilling"}
            </span>
            {isPending ? (
              <Spinner
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 m-auto"
              />
            ) : null}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Keeps the picks in the order they were made, so a new pick goes into the
// next empty slot instead of in front of earlier picks.
const usePickOrder = (selectedSkus: string[]) => {
  const [order, setOrder] = useState(selectedSkus);
  const next = [
    ...order.filter((sku) => selectedSkus.includes(sku)),
    ...selectedSkus.filter((sku) => !order.includes(sku)),
  ];
  if (next.join() !== order.join()) setOrder(next);
  return next;
};

export const GiftSelectionBar = ({
  backHref,
  isPending,
}: {
  backHref: string;
  isPending?: boolean;
}) => {
  const { control } = useFormContext<GiftSelectionFormValues>();
  const sections = useWatch({ control, name: "giftSections" }) ?? [];
  const pickOrder = usePickOrder(
    sections.flatMap(({ options }) =>
      options.filter((option) => option.selected).map(({ sku }) => sku),
    ),
  );

  return (
    <GiftSelectionBarView
      status={getGiftSelectionStatus(sections, pickOrder)}
      backHref={backHref}
      isPending={isPending}
    />
  );
};
